// libraries
import dayjs from '../utils/dayjs-config'
import { useEffect, useState } from 'react'
// redux
import { useSelector } from 'react-redux'
// router
import { useLocation } from 'react-router-dom'
// assets
import modalImg from 'assets/images/notification/tu_condicion@2x.png'
import Button from 'components/atoms/button/button'
// components
import Modal from 'components/atoms/modal/modal'
// types
import typesSlideOverlay from 'components/organisms/slide-overlay/types-slide-overlay'
// constants
import storageNames from 'constants/storageNames'
import AuthService from 'modules/Auth/services/auth'
// services
import NotificationsService from 'services/notifications'
// utils
import sendGaPush from 'utils/sendGaPush'
import useRouterControl from './useRouterControl'
// hooks
import useUIActions from './useUIActions'
import useNotificationQueries from './useNotificationQueries'

export default function useNotification({
	modalContent = false,
	listener,
	geaAction = 'ClicNotificacion'
} = {}) {
	// redux
	const {
		session: { email, userId }
	} = useSelector(state => state)

	// Cache de Tanstack Query (mantiene la lógica existente)
	const { getCachedNotifications, getCachedNotificationsConfig, invalidateNotifications } =
		useNotificationQueries(userId, email)

	// location
	const { pathname } = useLocation()
	const isParQPublicPath = pathname.includes('/par-q-public')

	// data
	const dateFormat = 'DD MMM'
	const dateFormatGlobal = 'DD/MM/YYYY'
	const today = dayjs().format(dateFormatGlobal)
	const tomorrow = dayjs().add(1, 'day').format(dateFormatGlobal)

	// state
	const [notificationState, setNotificationState] = useState({
		loading: false,
		config: false,
		notifications: false,
		pendingNotification: false,
		setModal: false,
		isStep: 0,
		showNotification: false
	})

	// state actions
	const setModal = modal =>
		setNotificationState({
			...notificationState,
			setModal: modal
		})

	const removeModal = () =>
		setNotificationState({
			...notificationState,
			setModal: false
		})

	const updateConfig = config =>
		setNotificationState({
			...notificationState,
			config,
			setModal: false
		})

	const handlesLoad = state =>
		setNotificationState({
			...notificationState,
			loading: state
		})

	const handledStep = step =>
		setNotificationState({
			...notificationState,
			isStep: step
		})

	const setShowNotification = notification => {
		setNotificationState({
			...notificationState,
			showNotification: notification
		})
	}

	const updateShowNotificationList = id =>
		setNotificationState({
			...notificationState,
			pendingNotification:
				notificationState.pendingNotification.filter(e => e.notificationId !== id) || false,
			showNotification:
				dayjs(sessionStorage.getItem(storageNames.notification)).format(dateFormatGlobal) > today
					? false
					: notificationState.showNotification
		})

	// hooks
	const { showOverlay, hideOverlay, handleShowModal } = useUIActions()
	const { routerControl } = useRouterControl()

	// metrics
	const handleSendGaPush = ({ action = geaAction, label }) => {
		sendGaPush({ category: 'SaludContinuarediseno', action, label })
	}

	// actions
	const showNotificationsOverlay = () => {
		return showOverlay({
			open: true,
			template: typesSlideOverlay.notification,
			showIconBack: false
		})
	}

	const showNotificationModal = () => {
		handleShowModal(
			<Modal
				imagen={modalImg}
				title='¡Nueva herramienta para ti!'
				description='Activa ya el nuevo kit de entorno seguro que traemos para ti y el cuidado de tus seres queridos'
				notification
			>
				<Button title='QUIERO SABER MÁS' uppercase classes='mt2 white' />
			</Modal>
		)
	}

	const goToNotification = ({ url, id, setLocal = false }) => {
		updateNotificationsSeen({
			id,
			callBack: () => {
				setLocal && localStorage.setItem(storageNames.notification, tomorrow)
				routerControl({ link: url })
				hideOverlay()
			}
		})
	}

	const closeNotification = ({ id, setLocal = false }) => {
		updateNotificationsSeen({
			id,
			callBack: () => {
				setLocal && localStorage.setItem(storageNames.notification, tomorrow)
			}
		})
	}

	const setDay = date => {
		const toDay = dayjs().format(dateFormat)
		const yesterday = dayjs().subtract(1, 'day').format(dateFormat)
		const isDate = dayjs(date).format(dateFormat)

		if (isDate === toDay) return 'Hoy'
		if (isDate === yesterday) return 'Ayer'
		return isDate
	}

	// calls - AHORA CON CACHE
	const getAllData = async () => {
		await handlesLoad(true)
		let notificationData = false
		let configData = false

		await getCachedNotificationsConfig().then(res => {
			if (res.status === 200) {
				configData = res.data
			}
		})

		await getCachedNotifications().then(res => {
			if (res.status === 200) {
				notificationData = res.data
			}
		})

		notificationData &&
			Array.isArray(notificationData) &&
			notificationData.sort((a, b) =>
				dayjs(b.generationDate).format(dateFormatGlobal) <
				dayjs(a.generationDate).format(dateFormatGlobal)
					? -1
					: 1
			)

		setNotificationState({
			...notificationState,
			config: configData,
			notifications: notificationData,
			pendingNotification:
				notificationData && notificationData.length > 0 ? notificationData.filter(e => !e.seen) : []
		})
	}

	const updateConfigNotification = ({ id, currentActivated }) => {
		const payload = {
			userConfigurationId: id,
			activated: !currentActivated
		}
		NotificationsService.updateNotificationConfig({ payload, userId }).then(res => {
			if (res.status === 200) {
				const newConfig = notificationState.config.map(e =>
					e.userConfigurationId === id ? { ...e, active: !currentActivated } : e
				)
				updateConfig(newConfig)
			}
		})
	}

	const updateNotificationsSeen = ({ id, callBack }) => {
		const payload = {
			email,
			notificationInformation: {
				notificationId: id
			}
		}
		NotificationsService.updateNotificationSeen({ payload, userId, notificationId: id }).then(
			res => {
				if (res.status === 200) {
					// Invalidar cache después de marcar como vista
					invalidateNotifications()
					callBack && callBack()
					updateShowNotificationList(id)
				}
			}
		)
	}

	const openModalConfiguration = ({ id, currentActivated }) => {
		if (!currentActivated) {
			return updateConfigNotification({ id, currentActivated })
		}

		if (!modalContent) return null

		const { img, title, description, buttons } = modalContent

		return setModal(
			<Modal imagen={img.url} title={title} description={description} onClose={() => removeModal()}>
				{buttons.map(e => (
					<Button
						key={e.tituloBoton}
						title={e.tituloBoton}
						uppercase
						classes={`mt1 ${e.tituloBoton === 'NO' && 'pink'}`}
						onClick={() => {
							handleSendGaPush({ label: `Configuración respuesta - ${e.tituloBoton}` })
							e.tituloBoton !== 'NO'
								? updateConfigNotification({ id, currentActivated })
								: removeModal()
						}}
					/>
				))}
			</Modal>
		)
	}

	const validDateToShowNotification = () => {
		const getFromLocal = localStorage.getItem(storageNames.notification)
		if (getFromLocal === null) return true
		return today > getFromLocal
	}

	useEffect(() => {
		if (
			!notificationState.pendingNotification ||
			notificationState.pendingNotification.length === 0
		) {
			return null
		}

		if (!validDateToShowNotification()) return null

		const isReady =
			notificationState.pendingNotification[notificationState.pendingNotification.length - 1]

		const setNotification =
			isReady &&
			setTimeout(() => {
				setShowNotification({
					title: isReady.title,
					description: isReady.description,
					buttonTitle: 'CONOCER MÁS',
					link: isReady.url,
					id: isReady.notificationId
				})
			}, 3000)

		return () => clearTimeout(setNotification)
	}, [notificationState.pendingNotification])

	useEffect(async () => {
		if (!AuthService.isLoggedIn(true) || isParQPublicPath) return null
		getAllData()

		return () => clearTimeout(closeNotification)
	}, [email, listener])

	// export
	const actions = {
		showNotificationsOverlay,
		openModalConfiguration,
		handledStep,
		closeNotification,
		setDay,
		goToNotification,
		showNotificationModal,
		updateNotificationsSeen
	}

	return {
		...actions,
		...notificationState
	}
}
